import { useMutation } from '@tanstack/react-query';
import type { UseMutateFunction } from '@tanstack/react-query';
import { API_CORE_URL, axiosInt, downloadFromUrl } from '../utils';
import { useMessage } from './useMessage';

export const GET_FILE_PRESIGNED_URL_KEY = 'getFilePresignedUrl';

interface IDownloadFileParams {
  fileId: string;
  modelInstanceId: string;
}

export const getFilePresignedUrlApi = async (
  fileId: string,
  modelInstanceId: string
): Promise<string> => {
  const response = await axiosInt.post(
    `${API_CORE_URL}/model-instances/${modelInstanceId}/files/download-url`,
    {
      fileId,
    }
  );
  if (typeof response.data === 'string') {
    return response.data;
  }

  if (response.data.url) {
    return response.data.url;
  }

  return response.data;
};

export const useDownloadFile = () => {
  const message = useMessage();

  const {
    mutate,
    isPending: isDownloadingFile,
    error: errorDownloadingFile,
  } = useMutation<string, Error, IDownloadFileParams>({
    mutationKey: [GET_FILE_PRESIGNED_URL_KEY],
    mutationFn: ({ fileId, modelInstanceId }) =>
      getFilePresignedUrlApi(fileId, modelInstanceId),
    onSuccess: url => {
      downloadFromUrl(url);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.title ||
        error?.message ||
        'Failed to download file';
      message.error(errorMessage);
    },
  });

  const downloadFile: UseMutateFunction<
    string,
    Error,
    IDownloadFileParams,
    unknown
  > = mutate;

  return {
    downloadFile,
    isDownloadingFile,
    errorDownloadingFile,
  };
};
